import { useState } from "react";
import { Download, Save } from "lucide-react";
import { http, errMsg, downloadFile } from "../../api";

export default function ProfileStep({ profile, languages, onSaved }) {
  const [phone, setPhone] = useState(profile.phone || "");
  const [lang, setLang] = useState(profile.regional_language || "");
  const [aadhaar, setAadhaar] = useState("");
  const [resume, setResume] = useState(null);
  const [idDoc, setIdDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const hasAadhaar = !!profile.aadhaar_masked;

  const submit = async e => {
    e.preventDefault();
    if (!profile.resume && !resume) return setNotice({ tone: "error", text: "Upload your resume (PDF, DOC or DOCX under 5 MB)." });
    if (!profile.aadhaar_doc && !idDoc) return setNotice({ tone: "error", text: "Upload a photo or PDF of your Aadhaar card." });
    if (!hasAadhaar && aadhaar.replace(/\D/g, "").length !== 12) return setNotice({ tone: "error", text: "Aadhaar number must be exactly 12 digits." });
    const fd = new FormData();
    fd.append("phone", phone); fd.append("regional_language", lang); fd.append("aadhaar_number", aadhaar.replace(/\D/g, ""));
    if (resume) fd.append("resume", resume);
    if (idDoc) fd.append("aadhaar_doc", idDoc);
    setBusy(true); setNotice(null);
    try { await http.post("/creator/profile", fd); setNotice({ tone: "success", text: "Details saved. Step 2 is unlocked." }); setResume(null); setIdDoc(null); onSaved(); }
    catch (err) { setNotice({ tone: "error", text: errMsg(err) }); }
    finally { setBusy(false); }
  };

  return (
    <form className="form-card" onSubmit={submit} data-testid="creator-profile-form">
      <h2>Step 1 · Fill your details</h2>
      <p>Name and email come from your Google account. Add your phone number, resume, Aadhaar details and the one regional language you work in besides English. Documents stay private to you and the Vocalhouse team.</p>
      <div className="field-row">
        <div><label>Full name</label><input value={profile.name} readOnly data-testid="creator-profile-name" /></div>
        <div><label>Email (Google verified)</label><input value={profile.email} readOnly data-testid="creator-profile-email" /></div>
      </div>
      <div className="field-row">
        <div><label>Phone number *</label><input required value={phone} onChange={e => setPhone(e.target.value)} placeholder="+91 98765 43210" data-testid="creator-phone-input" /></div>
        <div>
          <label>Regional language * (English is compulsory)</label>
          <select required value={lang} onChange={e => setLang(e.target.value)} data-testid="creator-language-select">
            <option value="">Select one language</option>
            {languages.map(l => <option key={l}>{l}</option>)}
          </select>
        </div>
      </div>
      <div className="tag-row" style={{ marginBottom: 14 }} data-testid="creator-language-pills">
        <span className="badge primary">English · compulsory</span>
        {lang && <span className="badge info">{lang}</span>}
      </div>
      <div className="field-row">
        <div>
          <label>Aadhaar number * {hasAadhaar && <span className="muted">(saved: {profile.aadhaar_masked} — enter again only to change)</span>}</label>
          <input inputMode="numeric" maxLength={14} value={aadhaar} onChange={e => setAadhaar(e.target.value.replace(/[^0-9 ]/g, ""))} placeholder={hasAadhaar ? profile.aadhaar_masked : "1234 5678 9012"} required={!hasAadhaar} data-testid="creator-aadhaar-input" />
        </div>
        <div>
          <label>Aadhaar photo ID * (JPG, PNG or PDF under 5 MB)</label>
          <input type="file" accept=".jpg,.jpeg,.png,.webp,.pdf" onChange={e => setIdDoc(e.target.files?.[0] || null)} data-testid="creator-aadhaar-doc-input" />
          {profile.aadhaar_doc && <div className="file-hint" data-testid="creator-aadhaar-current">Uploaded: <strong>{profile.aadhaar_doc.filename}</strong><button type="button" className="link" onClick={() => downloadFile("/creator/aadhaar", {}, profile.aadhaar_doc.filename)} data-testid="creator-aadhaar-download"><Download size={12} /> View</button></div>}
        </div>
      </div>
      <div>
        <label>Resume * (PDF, DOC or DOCX under 5 MB)</label>
        <input type="file" accept=".pdf,.doc,.docx" onChange={e => setResume(e.target.files?.[0] || null)} data-testid="creator-resume-input" />
        {profile.resume && <div className="file-hint" data-testid="creator-resume-current">Current resume: <strong>{profile.resume.filename}</strong><button type="button" className="link" onClick={() => downloadFile("/creator/resume", {}, profile.resume.filename)} data-testid="creator-resume-download"><Download size={12} /> Download</button></div>}
      </div>
      {notice && <div className={`notice ${notice.tone}`} style={{ marginTop: 14 }} data-testid="creator-profile-notice">{notice.text}</div>}
      <button className="primary" disabled={busy} style={{ marginTop: 14 }} data-testid="creator-profile-save"><Save size={15} /> {busy ? "Saving…" : "Save details"}</button>
    </form>
  );
}
